import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import type { MailVarContents } from '@adornis/mails/db/mail-template.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { ComponentParamaterType, ComponentType, MessageType } from '@adornis/whatsapp/server/types.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import {
  getWhatsappTemplate,
  InstitutionMailTemplate,
  MailTemplateType,
  MailTemplateVars,
} from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsapp } from './_helper.js';

// Mail template for Nutzer-Sperrung
defineMailTemplate(MailTemplateType.SPERRUNG, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Sperrung durch Veranstalter',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>wir haben Sie soeben von unseren Veranstaltungen abgemeldet, da Sie in einer oder mehreren Veranstaltungen unentschuldigt gefehlt haben. Ihr Fehlen hat zur Folge, dass Sie im laufenden Semester gesperrt wurden und Sie gleichzeitig alle Pl&auml;tze in anderen Veranstaltungen verlieren. Im n&auml;chsten Semester k&ouml;nnen Sie sich wieder f&uuml;r Veranstaltungen anmelden.</p>
<p>Hinweis: Falls uns ein Fehler unterlaufen sein sollte, bitten wir Sie, sich mit uns <a href="mailto:contact@anmeldung.uni-frankfurt.de">per Mail</a> in Verbindung zu setzen.</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>You just have been deregistered from our courses because you have been absent from one or more courses without excuse. Your absence has resulted in you bing blocked for the current semester, and at the same time you will los all seats in other classes. You will be able to register for events again next semester.</p>
<p>If we have made a mistake, please contact us by <a href="mailto:contact@anmeldung.uni-frankfurt.de">mail</a>.</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.SPERRUNG),
  attachments: [],
  displayName: 'Sperrung',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.SPERRUNG),
});

export async function notifyNuzterSperrung(participationID: string) {
  const participation = await getByID(
    Participation,
    participationID,
  )({
    _id: 1,
    _class: 1,
    userID: 1,
    productID: 1,
    product: { _id: 1, name: 1, institutionID: 1 },
    user: { _id: 1, title: 1, firstName: 1, lastName: 1, email: 1, whatsappNotification: 1, whatsappPhone: 1 },
  });
  if (!participation) throw new Error('Couldnt find participation with ID ' + participationID);

  const user = participation.user;
  if (!user) throw new Error('no user is set');

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const institutionID = participation.product?.institutionID;

  // Get institution with color definitions
  const institution = await getByID(
    Institution,
    institutionID,
  )({
    _id: 1,
    name: 1,
    colorDefintions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });

  // Set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
    institutionID,
    MailTemplateType.SPERRUNG,
  )(InstitutionMailTemplate.allFields);

  const notificationType = getNotificationType(user, mailTemplate);
  if (notificationType === 'whatsapp') {
    await sendWhatsapp({
      to: user.whatsappPhone!,
      type: MessageType.TEMPLATE,
      content: {
        template: {
          name: getWhatsappTemplate(MailTemplateType.SPERRUNG).name,
          language: {
            code: 'de',
          },
          components: [
            {
              type: ComponentType.BODY,
              parameters: [
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapFirstNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapLastNameVar(user) || '-',
                },
              ],
            },
          ],
        },
      },
    });
  } else if (notificationType === 'email') {
    const mailVars = {
      [MailTemplateVars.TITLE]: participation.user?.title ?? '',
      [MailTemplateVars.FIRST_NAME]: participation.user?.firstName,
      [MailTemplateVars.LAST_NAME]: participation.user?.lastName,
      [MailTemplateVars.EVENT_TITLE]: participation.product?.name,
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
    } as MailVarContents;

    const content = await mailTemplate.buildEmail(mailVars as any);
    const subject = await mailTemplate.buildSubject(mailVars as any);

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({
      email: user.email,
      institutionID,
      content,
      subject,
      attachmentPaths: [] as string[],
    });
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
